/**
 * ChartPlugin - registers ChartNode and exposes INSERT_CHART_COMMAND
 * along with an InsertChartDialog used by the toolbar's Insert menu.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
} from 'lexical';
import { useEffect, useState } from 'react';
import * as React from 'react';

import { $createChartNode, ChartNode } from '../../nodes/ChartNode';
import { $isChartNode } from '../../nodes/chartShared';
import {
  CHART_TYPES,
  createDefaultChartData,
} from '../../nodes/chartShared';
import { CAN_USE_DOM } from '../../shared/canUseDOM';
import Button from '../../ui/Button';
import { DialogActions } from '../../ui/Dialog';
import Select from '../../ui/Select';
import TextInput from '../../ui/TextInput';

export const INSERT_CHART_COMMAND = createCommand('INSERT_CHART_COMMAND');

const getDOMSelection = (targetWindow) =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const dragImg = document.createElement('img');
dragImg.src = TRANSPARENT_IMAGE;

function $getChartNodeInSelection() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) return null;
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isChartNode(node) ? node : null;
}

function $onDragStart(event) {
  const node = $getChartNodeInSelection();
  if (!node) return false;
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) return false;
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(dragImg, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({ type: 'chart', data: { key: node.getKey(), ...node.getChartData() } }),
  );
  return true;
}

// During dragover/drop the Lexical selection has already moved away from the
// chart node.  We detect a chart drag via the DataTransfer types list (reading
// the actual value is not allowed during dragover) and via the serialised
// payload during drop.
function $onDragover(event) {
  const isChartDrag =
    event.dataTransfer?.types?.includes('application/x-lexical-drag') ?? false;
  if (!isChartDrag) return false;
  if (!canDropChart(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event, editor) {
  // Read transfer data first — selection is no longer on the chart at this point.
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) return false;

  let parsed;
  try {
    parsed = JSON.parse(dragData);
  } catch {
    return false;
  }
  if (parsed.type !== 'chart') return false;

  event.preventDefault();

  if (!canDropChart(event)) return true;

  const { key, ...chartData } = parsed.data;

  // Locate the original node by the key stored in the transfer data.
  const node = $getNodeByKey(key);
  if (!$isChartNode(node)) return false;

  const range = getDragSelection(event);
  node.remove();

  const rangeSelection = $createRangeSelection();
  if (range != null) {
    rangeSelection.applyDOMRange(range);
  }
  $setSelection(rangeSelection);
  editor.dispatchCommand(INSERT_CHART_COMMAND, chartData);

  return true;
}

function canDropChart(event) {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('.ChartNode__container') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  );
}

function getDragSelection(event) {
  let range;
  const target = event.target;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
      ? target.defaultView
      : target.ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw new Error('Cannot get the selection when dragging');
  }
  return range;
}

const CHART_TYPE_LABELS = {
  bar: 'Bar Chart',
  line: 'Line Chart',
  area: 'Area Chart',
  pie: 'Pie Chart',
};

export function InsertChartDialog({ activeEditor, onClose }) {
  const [chartType, setChartType] = useState('bar');
  const [title, setTitle] = useState('Untitled Chart');

  const onClick = () => {
    const data = createDefaultChartData(chartType);
    data.title = title;
    activeEditor.dispatchCommand(INSERT_CHART_COMMAND, data);
    onClose();
  };

  return (
    <>
      <Select
        label="Chart type"
        name="chart-type"
        value={chartType}
        onChange={(e) => setChartType(e.target.value)}
      >
        {CHART_TYPES.map((t) => (
          <option key={t} value={t}>
            {CHART_TYPE_LABELS[t]}
          </option>
        ))}
      </Select>
      <TextInput label="Title" onChange={setTitle} value={title} />
      <DialogActions>
        <Button onClick={onClick}>Insert</Button>
      </DialogActions>
    </>
  );
}

export default function ChartPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ChartNode])) {
      throw new Error('ChartPlugin: ChartNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_CHART_COMMAND,
        (payload) => {
          const chartNode = $createChartNode(payload);
          $insertNodes([chartNode]);
          if ($isRootOrShadowRoot(chartNode.getParentOrThrow())) {
            $wrapNodeInElement(chartNode, $createParagraphNode).selectEnd();
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => $onDragStart(event),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => $onDragover(event),
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => $onDrop(event, editor),
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
}
